import React, { useState } from 'react';
import toast from 'react-hot-toast';
import jobListingsService from '../../services/JobListingsService';

const NEW_OPTION = '__new__';

const CONTROL_CLASSES =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Shows a job's Bereich and lets it be changed inline.
 *
 * The import never sets a department — the Google-Jobs JSON has no such field —
 * so every freshly imported listing needs one picked here or in the CMS.
 */
export function DepartmentPicker({ job, departments, canEdit, onSaved, onDepartmentCreated }) {
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  async function assign(departmentId) {
    setSaving(true);
    const result = await jobListingsService.setJobDepartment(job.id, departmentId, {
      isDraft: job.status === 'draft',
    });
    setSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    toast.success('Bereich gespeichert');
    setEditing(false);
    setCreating(false);
    setNewName('');
    onSaved?.();
  }

  async function createAndAssign() {
    setSaving(true);
    const created = await jobListingsService.createDepartment(newName);
    if (!created.success) {
      setSaving(false);
      toast.error(created.error);
      return;
    }
    onDepartmentCreated?.(created.data);
    await assign(created.data.id);
  }

  function handleSelect(value) {
    if (value === NEW_OPTION) {
      setCreating(true);
      return;
    }
    assign(value || null);
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        {job.department ? (
          <span className="truncate">{job.department}</span>
        ) : (
          <span className="text-amber-600 dark:text-amber-400">Kein Bereich</span>
        )}
        {/* Hidden without a CMS key — the PATCH would only come back as 403. */}
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            ändern
          </button>
        )}
      </div>
    );
  }

  if (creating) {
    return (
      <div className="flex items-center gap-1.5 w-full">
        <input
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !saving && newName.trim() && createAndAssign()}
          placeholder="Name des neuen Bereichs"
          className={CONTROL_CLASSES}
        />
        <button
          type="button"
          disabled={saving || !newName.trim()}
          onClick={createAndAssign}
          className="shrink-0 rounded-lg bg-blue-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 hover:bg-blue-700 transition-colors"
        >
          Anlegen
        </button>
        <button
          type="button"
          onClick={() => { setCreating(false); setNewName(''); }}
          className="shrink-0 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Abbrechen
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 w-full">
      <select
        autoFocus
        disabled={saving}
        value={job.departmentId || ''}
        onChange={(e) => handleSelect(e.target.value)}
        className={CONTROL_CLASSES}
      >
        <option value="">Kein Bereich</option>
        {departments.map((dept) => (
          <option key={dept.id} value={dept.id}>
            {dept.name}
          </option>
        ))}
        <option value={NEW_OPTION}>+ Neuen Bereich anlegen …</option>
      </select>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="shrink-0 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        Abbrechen
      </button>
    </div>
  );
}
